import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { useFirmId } from "../FirmProvider";
import sortBy from "lodash/sortBy";
import { getAllFirms, getFirm } from "./firmApi";
import { useOnPropagate } from "../../api/PropagateProvider";

const GET_FIRM_KEY = "getFirm"; // used by multiple hooks

function useFirmPropagation() {
    const key = GET_FIRM_KEY;

    useOnPropagate(key, p => {
        p.onSingleDefaults("firm", x => [key, x.id]);
    });
}

export const useFirm = (firmId: string) => {
    const key = GET_FIRM_KEY;

    const { data: firm } = useSuspenseQuery({
        queryKey: [key, firmId],
        queryFn: () => getFirm(firmId)
    });

    useFirmPropagation();

    return firm;
};

export const useCurrentFirm = () => {
    const { firmId } = useFirmId();
    return useFirm(firmId);
};

export const useFirms = () => {
    const key = "getAllFirms";

    const { data: rawFirms } = useSuspenseQuery({
        queryKey: [key],
        queryFn: getAllFirms
    });

    const firms = sortBy(rawFirms, x => x.name.toLocaleLowerCase());

    useOnPropagate(key, p => {
        p.onCollectionDefaults("firm", [key]);
    });

    return firms;
};

export const useMultipleFirms = (firmIds: string[]) => {
    const key = GET_FIRM_KEY;

    const result = useSuspenseQueries({
        queries: firmIds.map(id => ({
            queryKey: [key, id],
            queryFn: () => getFirm(id)
        }))
    });

    useFirmPropagation();

    return result.map(x => x.data);
};
// re-export for prefetching (react-compiler eslint doesn't like passing hooks as reference)
export const reexported__useFirm = useFirm;
export const reexported__useCurrentFirm = useCurrentFirm;
export const reexported__useMultipleFirms = useMultipleFirms;
